import { motion, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import CancelIcon from "@mui/icons-material/Cancel";
import PersonIcon from "@mui/icons-material/Person";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import BusinessIcon from "@mui/icons-material/Business";

interface IModalVerSolicitud {
  onClose: () => void;
  detalleData: any;
  onResponder?: (params: { aceptar: boolean; comentario: string }) => Promise<void>;
}

const formatDateTime = (isoString: string | null): string => {
  if (!isoString) return "—";
  const d = new Date(isoString);
  if (isNaN(d.getTime())) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} ${hh}:${min}`;
};

const formatDate = (dateString: string | null): string => {
  if (!dateString) return "—";
  const parts = dateString.split("-");
  if (parts.length !== 3) return "—";
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
};

const calcularEdad = (fechaNacimiento: string | null): number | string => {
  if (!fechaNacimiento) return "—";
  const hoy = new Date();
  const nacimiento = new Date(fechaNacimiento);
  if (isNaN(nacimiento.getTime())) return "—";
  let edad = hoy.getFullYear() - nacimiento.getFullYear();
  const m = hoy.getMonth() - nacimiento.getMonth();
  if (m < 0 || (m === 0 && hoy.getDate() < nacimiento.getDate())) {
    edad--;
  }
  return edad;
};

const STATUS_CONFIG: Record<
  string,
  {
    bg: string;
    color: string;
    border: string;
    icon: JSX.Element;
    label: string;
    headerGradient: string;
    headerBorder: string;
    iconColor: string;
    iconBg: string;
    calendarColor: string;
    dividerColor: string;
  }
> = {
  ENVIADA: {
    bg: "#DBEAFE",
    color: "#1D4ED8",
    border: "#93C5FD",
    icon: <SendIcon sx={{ fontSize: 14 }} />,
    label: "ENVIADA",
    headerGradient: "linear-gradient(135deg, #1E3A8A 0%, #1D4ED8 30%, #2563EB 55%, #1D4ED8 80%, #1E3A8A 100%)",
    headerBorder: "#93C5FD",
    iconColor: "#0065DD",
    iconBg: "rgba(255,255,255,0.25)",
    calendarColor: "#BFDBFE",
    dividerColor: "rgba(255,255,255,0.35)",
  },
  APROBADA: {
    bg: "#DCFCE7",
    color: "#15803D",
    border: "#86EFAC",
    icon: <TaskAltIcon sx={{ fontSize: 14 }} />,
    label: "APROBADA",
    headerGradient: "linear-gradient(135deg, #064E3B 0%, #065F46 30%, #059669 55%, #065F46 80%, #064E3B 100%)",
    headerBorder: "#6ee7b7",
    iconColor: "#059669",
    iconBg: "rgba(255,255,255,0.25)",
    calendarColor: "#A7F3D0",
    dividerColor: "rgba(255,255,255,0.35)",
  },
  RECHAZADA: {
    bg: "#FFE4E6",
    color: "#BE123C",
    border: "#FCA5A5",
    icon: <CancelIcon sx={{ fontSize: 14 }} />,
    label: "RECHAZADA",
    headerGradient: "linear-gradient(135deg, #7F1D1D 0%, #991B1B 30%, #DC2626 55%, #991B1B 80%, #7F1D1D 100%)",
    headerBorder: "#fca5a5",
    iconColor: "#DC2626",
    iconBg: "rgba(255,255,255,0.25)",
    calendarColor: "#FECACA",
    dividerColor: "rgba(255,255,255,0.35)",
  },
};

const InfoField = ({
  label,
  value,
  wide,
}: {
  label: string;
  value: string | number;
  wide?: boolean;
}) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: "2px",
      gridColumn: wide ? "span 2" : undefined,
    }}
  >
    <span
      style={{
        fontSize: "10px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.7px",
        color: "#94A3B8",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {label}
    </span>
    <span
      style={{
        fontSize: "14px",
        fontWeight: 500,
        color: "#1E293B",
        fontFamily: "Inter, sans-serif",
        wordBreak: "break-all",
      }}
    >
      {value !== null && value !== undefined && value !== "" ? value : "—"}
    </span>
  </div>
);

const SectionTitle = ({ icon, title }: { icon: JSX.Element; title: string }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "12px",
      paddingBottom: "7px",
      borderBottom: "1px solid #E2E8F0",
    }}
  >
    <span style={{ color: "#0065DD", display: "flex", alignItems: "center" }}>
      {icon}
    </span>
    <span
      style={{
        fontSize: "12px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#334155",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {title}
    </span>
  </div>
);

const PROCESS_COLORS = {
  origen: { accent: "#D97706", bg: "#FFFBEB", border: "#FDE68A", label: "#92400E" },
  destino: { accent: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE", label: "#5B21B6" },
};

const ProcessCard = ({
  tipo,
  id,
  cargo,
  reclutador,
  cliente,
  areaNegocio,
}: {
  tipo: "origen" | "destino";
  id: string;
  cargo: string;
  reclutador: string;
  cliente: string;
  areaNegocio: string;
}) => {
  const c = PROCESS_COLORS[tipo];
  return (
    <div
      style={{
        flex: 1,
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: "10px",
        padding: "14px",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "3px",
          background: c.accent,
          borderRadius: "10px 10px 0 0",
        }}
      />
      <div
        style={{
          fontSize: "10px",
          fontWeight: 700,
          color: c.label,
          textTransform: "uppercase",
          letterSpacing: "0.7px",
          marginBottom: "10px",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {tipo === "origen" ? "Origen" : "Destino"}
      </div>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "8px 16px",
        }}
      >
        <InfoField label="ID Proceso" value={id} />
        <InfoField label="Cargo" value={cargo} wide />
        <InfoField label="Cliente" value={cliente} wide />
        <InfoField label="Área de Negocio" value={areaNegocio} />
        <InfoField label="Reclutador" value={reclutador} />
      </div>
    </div>
  );
};

const CommentBox = ({ label, value }: { label: string; value: string | null }) => {
  const displayValue = value || "—";
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
      <span
        style={{
          fontSize: "10px",
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: "0.6px",
          color: "#64748B",
          fontFamily: "Inter, sans-serif",
        }}
      >
        {label}
      </span>
      <div
        style={{
          background: "#F8FAFC",
          border: "1px solid #E2E8F0",
          borderRadius: "8px",
          padding: "10px 12px",
          fontSize: "14px",
          fontWeight: 400,
          color: displayValue === "—" ? "#94A3B8" : "#334155",
          fontFamily: "Inter, sans-serif",
          lineHeight: "1.55",
          fontStyle: displayValue === "—" ? "italic" : "normal",
          minHeight: "48px",
        }}
      >
        {displayValue}
      </div>
    </div>
  );
};

const isDesktop = typeof window !== "undefined" && window.innerWidth > 768;

const backdropVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.22 } },
  exit: { opacity: 0, transition: { duration: 0.18, delay: 0.05 } },
};

const modalVariants = isDesktop
  ? {
      hidden: { opacity: 0, scale: 0.90, y: 52, rotateX: 10 },
      visible: {
        opacity: 1,
        scale: 1,
        y: 0,
        rotateX: 0,
        transition: { type: "spring" as const, stiffness: 260, damping: 22 },
      },
      exit: {
        opacity: 0,
        scale: 0.94,
        y: 28,
        transition: { duration: 0.17 },
      },
    }
  : {
      hidden: { opacity: 0, y: 24 },
      visible: { opacity: 1, y: 0, transition: { duration: 0.2 } },
      exit: { opacity: 0, y: 12, transition: { duration: 0.15 } },
    };

const bodyContainerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: isDesktop ? 0.2 : 0,
    },
  },
};

const bodySectionVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: "spring" as const, stiffness: 360, damping: 28 },
  },
};

const ModalVerSolicitud = ({ onClose, detalleData, onResponder }: IModalVerSolicitud) => {
  const statusCfg = STATUS_CONFIG[detalleData.estado] ?? STATUS_CONFIG["ENVIADA"];
  const [comentarioRespuesta, setComentarioRespuesta] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const mouseDownOnBackdrop = useRef(false);

  const canResponder = !!onResponder && detalleData.estado === "ENVIADA";

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const handleResponder = async (aceptar: boolean) => {
    if (!onResponder) return;
    setIsSubmitting(true);
    await onResponder({ aceptar, comentario: comentarioRespuesta });
    setIsSubmitting(false);
  };

  const rawX = useMotionValue(-300);
  const rawY = useMotionValue(-300);
  const glowX = useSpring(rawX, { stiffness: 120, damping: 18 });
  const glowY = useSpring(rawY, { stiffness: 120, damping: 18 });

  const nombres = [detalleData.primerNombre, detalleData.segundoNombre]
    .filter(Boolean)
    .join(" ");
  const apellidos = [detalleData.primerApellido, detalleData.segundoApellido]
    .filter(Boolean)
    .join(" ");

  return (
    <motion.div
        variants={backdropVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="traspaso-modal-backdrop"
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(15, 23, 42, 0.50)",
          backdropFilter: "blur(3px)",
          zIndex: 1300,
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "center",
          paddingTop: "44px",
          paddingBottom: "44px",
          paddingLeft: "20px",
          paddingRight: "20px",
          overflowY: "auto",
          perspective: isDesktop ? "1400px" : undefined,
        }}
        onMouseDown={(e) => {
          mouseDownOnBackdrop.current = e.target === e.currentTarget;
        }}
        onMouseUp={(e) => {
          if (mouseDownOnBackdrop.current && e.target === e.currentTarget) onClose();
          mouseDownOnBackdrop.current = false;
        }}
      >
        <motion.div
          variants={modalVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            background: "#FFFFFF",
            borderRadius: "18px",
            width: "100%",
            maxWidth: "1160px",
            boxShadow: "0 16px 48px rgba(0,0,0,0.12), 0 4px 14px rgba(0,0,0,0.06)",
            overflow: "hidden",
            fontFamily: "Inter, sans-serif",
            transformOrigin: "top center",
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* ── HEADER ── */}
          <style>{`
            @keyframes gradientShift {
              0%   { background-position: 0% 50%; }
              50%  { background-position: 100% 50%; }
              100% { background-position: 0% 50%; }
            }
          `}</style>
          <motion.div
            className="traspaso-modal-header"
            onMouseMove={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              rawX.set(e.clientX - rect.left);
              rawY.set(e.clientY - rect.top);
            }}
            onMouseLeave={() => {
              rawX.set(-300);
              rawY.set(-300);
            }}
            style={{
              background: statusCfg.headerGradient,
              backgroundSize: "400% 400%",
              animation: "gradientShift 11s ease infinite",
              borderBottom: `1px solid ${statusCfg.headerBorder}`,
              padding: "20px 24px 16px",
              position: "relative",
              overflow: "hidden",
            }}
          >
            {/* Shimmer sweep */}
            <motion.div
              animate={{ x: ["-100%", "220%"] }}
              transition={{ duration: 5, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" }}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "45%",
                height: "100%",
                background: "linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)",
                pointerEvents: "none",
                skewX: -12,
                zIndex: 0,
              }}
            />
            {/* Cursor glow */}
            <motion.div
              style={{
                position: "absolute",
                width: "340px",
                height: "340px",
                borderRadius: "50%",
                background: "radial-gradient(circle, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.02) 45%, transparent 70%)",
                pointerEvents: "none",
                left: glowX,
                top: glowY,
                translateX: "-50%",
                translateY: "-50%",
                zIndex: 0,
              }}
            />
            <button
              onClick={onClose}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 10,
                pointerEvents: "all",
                background: "rgba(255,255,255,0.15)",
                border: "none",
                borderRadius: "7px",
                width: "28px",
                height: "28px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#ffffff",
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.25)")
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.background = "rgba(255,255,255,0.15)")
              }
            >
              <CloseIcon sx={{ fontSize: 15 }} />
            </button>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                paddingRight: "36px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  background: statusCfg.iconBg,
                  borderRadius: "9px",
                  padding: "7px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <SwapHorizIcon sx={{ fontSize: 20, color: "#FFFFFF" }} />
              </div>
              <div>
                <p
                  style={{
                    margin: 0,
                    fontSize: "10px",
                    fontWeight: 500,
                    color: "rgba(255,255,255,0.75)",
                    letterSpacing: "0.9px",
                    textTransform: "uppercase",
                    marginBottom: "1px",
                  }}
                >
                  Detalle
                </p>
                <h2
                  style={{
                    margin: 0,
                    fontSize: "18px",
                    fontWeight: 700,
                    color: "#FFFFFF",
                    letterSpacing: "-0.2px",
                    textShadow: "0 1px 4px rgba(0,0,0,0.2)",
                  }}
                >
                  Solicitud de Traspaso
                </h2>
              </div>
            </div>

            <div
              className="traspaso-modal-header-meta"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "16px",
                marginTop: "14px",
                flexWrap: "wrap",
                position: "relative",
                zIndex: 1,
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "6px",
                  background: statusCfg.bg,
                  color: statusCfg.color,
                  border: `1.5px solid ${statusCfg.border}`,
                  borderRadius: "8px",
                  padding: "5px 13px",
                  fontSize: "11px",
                  fontWeight: 700,
                  letterSpacing: "0.7px",
                  textTransform: "uppercase",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                }}
              >
                {statusCfg.icon}
                Estado: {statusCfg.label}
              </div>

              <div className="traspaso-divider" style={{ width: "1px", height: "28px", background: statusCfg.dividerColor }} />

              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: statusCfg.calendarColor }} />
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "10px",
                      color: "rgba(255,255,255,0.7)",
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha Solicitud
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                    }}
                  >
                    {formatDateTime(detalleData.fechaSolicitud)}
                  </p>
                </div>
              </div>

              <div className="traspaso-divider" style={{ width: "1px", height: "28px", background: statusCfg.dividerColor }} />

              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <CalendarTodayIcon sx={{ fontSize: 13, color: statusCfg.calendarColor }} />
                <div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "10px",
                      color: "rgba(255,255,255,0.7)",
                      letterSpacing: "0.4px",
                      textTransform: "uppercase",
                    }}
                  >
                    Fecha Respuesta
                  </p>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#FFFFFF",
                    }}
                  >
                    {formatDateTime(detalleData.fechaRespuesta)}
                  </p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* ── BODY ── */}
          <motion.div
            className="traspaso-modal-body"
            variants={bodyContainerVariants}
            initial="hidden"
            animate="visible"
            style={{
              padding: "20px 24px 24px",
              display: "flex",
              flexDirection: "column",
              gap: "18px",
            }}
          >
            {/* CANDIDATO */}
            <motion.div variants={bodySectionVariants}>
              <SectionTitle
                icon={<PersonIcon sx={{ fontSize: 16 }} />}
                title="Información del Candidato"
              />
              <div
                className="traspaso-candidate-grid"
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "12px 24px",
                }}
              >
                <InfoField label="RUT" value={detalleData.personaDNI} />
                <InfoField label="Nombres" value={nombres} />
                <InfoField label="Apellidos" value={apellidos} />
                <InfoField label="Fecha de Nacimiento" value={formatDate(detalleData.fechaNacimiento)} />
                <InfoField label="Edad" value={calcularEdad(detalleData.fechaNacimiento)} />
                <InfoField label="Nacionalidad" value={detalleData.nacionalidad} />
                <InfoField label="Comuna" value={detalleData.comuna} />
              </div>
            </motion.div>

            {/* PROCESOS */}
            <motion.div variants={bodySectionVariants}>
              <SectionTitle
                icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                title="Procesos"
              />
              <div className="traspaso-process-cards" style={{ display: "flex", gap: "10px", alignItems: "stretch" }}>
                <ProcessCard
                  tipo="origen"
                  id={detalleData.procesoOrigen}
                  cargo={detalleData.cargoOrigen}
                  reclutador={detalleData.propietario}
                  cliente={detalleData.clienteOrigen}
                  areaNegocio={detalleData.areaNegocioOrigen}
                />
                <div
                  className="traspaso-swap-icon-wrapper"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    color: "#CBD5E1",
                    flexShrink: 0,
                  }}
                >
                  <SwapHorizIcon sx={{ fontSize: 18 }} />
                </div>
                <ProcessCard
                  tipo="destino"
                  id={detalleData.procesoDestino}
                  cargo={detalleData.cargoDestino}
                  reclutador={detalleData.solicitante}
                  cliente={detalleData.clienteDestino}
                  areaNegocio={detalleData.areaNegocioDestino}
                />
              </div>
            </motion.div>

            {/* COMENTARIOS */}
            <motion.div variants={bodySectionVariants}>
              <SectionTitle
                icon={<ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />}
                title="Comentarios"
              />
              <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
                <CommentBox
                  label="Comentario de Solicitud"
                  value={detalleData.comentarioSolicitud}
                />
                {canResponder ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: "5px" }}>
                    <span style={{
                      fontSize: "10px",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.6px",
                      color: "#64748B",
                      fontFamily: "Inter, sans-serif",
                    }}>
                      Comentario de Respuesta
                    </span>
                    <input
                      type="text"
                      placeholder="Escribe tu comentario de respuesta (opcional)..."
                      value={comentarioRespuesta}
                      onChange={(e) => setComentarioRespuesta(e.target.value)}
                      disabled={isSubmitting}
                      style={{
                        background: "#ffffff",
                        border: "1px solid #CBD5E1",
                        borderRadius: "8px",
                        padding: "10px 12px",
                        fontSize: "14px",
                        fontWeight: 400,
                        color: "#334155",
                        fontFamily: "Inter, sans-serif",
                        outline: "none",
                        width: "100%",
                        boxSizing: "border-box",
                        transition: "border-color 0.15s",
                      }}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "#0065DD"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "#CBD5E1"; }}
                    />
                  </div>
                ) : (
                  <CommentBox
                    label="Comentario de Respuesta"
                    value={detalleData.comentarioRespuesta}
                  />
                )}
              </div>
            </motion.div>

            {/* FOOTER */}
            <motion.div variants={bodySectionVariants} className="traspaso-modal-footer" style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <motion.button
                onClick={onClose}
                disabled={isSubmitting}
                style={{
                  backgroundColor: "#F1F5F9",
                  border: "1px solid #E2E8F0",
                  borderRadius: "9px",
                  padding: "8px 22px",
                  fontSize: "14px",
                  fontWeight: 600,
                  color: "#475569",
                  cursor: isSubmitting ? "not-allowed" : "pointer",
                  fontFamily: "Inter, sans-serif",
                  opacity: isSubmitting ? 0.6 : 1,
                }}
                whileHover={!isSubmitting ? {
                  scale: 1.03,
                  y: -2,
                  backgroundColor: "#E2E8F0",
                  color: "#1E293B",
                  boxShadow: "0 4px 14px rgba(0,0,0,0.10)",
                } : {}}
                whileTap={!isSubmitting ? { scale: 0.96, y: 0 } : {}}
                transition={{ type: "spring", stiffness: 380, damping: 22 }}
              >
                Cerrar
              </motion.button>
              {canResponder && (
                <>
                  <motion.button
                    onClick={() => handleResponder(false)}
                    disabled={isSubmitting}
                    style={{
                      background: "linear-gradient(135deg, #dc2626, #e11d48)",
                      border: "none",
                      borderRadius: "9px",
                      padding: "8px 22px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#ffffff",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      boxShadow: "0 2px 8px rgba(220,38,38,0.35)",
                      opacity: isSubmitting ? 0.6 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    whileHover={!isSubmitting ? {
                      scale: 1.05,
                      y: -3,
                      boxShadow: "0 8px 22px rgba(220,38,38,0.50)",
                    } : {}}
                    whileTap={!isSubmitting ? { scale: 0.96, y: 0 } : {}}
                    transition={{ type: "spring", stiffness: 380, damping: 22 }}
                  >
                    <CancelIcon sx={{ fontSize: 16 }} />
                    Rechazar
                  </motion.button>
                  <motion.button
                    onClick={() => handleResponder(true)}
                    disabled={isSubmitting}
                    style={{
                      background: "linear-gradient(135deg, #16a34a, #059669)",
                      border: "none",
                      borderRadius: "9px",
                      padding: "8px 22px",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#ffffff",
                      cursor: isSubmitting ? "not-allowed" : "pointer",
                      fontFamily: "Inter, sans-serif",
                      boxShadow: "0 2px 8px rgba(22,163,74,0.35)",
                      opacity: isSubmitting ? 0.6 : 1,
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                    }}
                    whileHover={!isSubmitting ? {
                      scale: 1.05,
                      y: -3,
                      boxShadow: "0 8px 22px rgba(22,163,74,0.50)",
                    } : {}}
                    whileTap={!isSubmitting ? { scale: 0.96, y: 0 } : {}}
                    transition={{ type: "spring", stiffness: 380, damping: 22 }}
                  >
                    <TaskAltIcon sx={{ fontSize: 16 }} />
                    Aprobar
                  </motion.button>
                </>
              )}
            </motion.div>
          </motion.div>
        </motion.div>
      </motion.div>
  );
};

export default ModalVerSolicitud;
